import { CheckCircle2, Download, RefreshCw, WifiOff, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { usePwaStore, type BeforeInstallPromptEvent } from "../../state/pwa-store";
import { Button } from "../ui/button";

export function PwaStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [recovered, setRecovered] = useState(false);
  const installPrompt = usePwaStore((state) => state.installPrompt);
  const setInstallPrompt = usePwaStore((state) => state.setInstallPrompt);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker
  } = useRegisterSW({
    onRegisterError(error) {
      if (import.meta.env.DEV) console.error("Service worker registration failed", error);
    }
  });

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      setRecovered(true);
      window.setTimeout(() => setRecovered(false), 3200);
    };
    const onOffline = () => setOnline(false);
    const onInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeinstallprompt", onInstall);
    };
  }, [setInstallPrompt]);

  if (!online) {
    return (
      <div className="fixed inset-x-0 top-[env(safe-area-inset-top)] z-[70] mx-auto flex min-h-11 max-w-lg items-center justify-center gap-2 bg-slate-900 px-4 text-sm font-medium text-white" role="status">
        <WifiOff className="size-4" />
        已离线，演示数据仍可使用
      </div>
    );
  }

  if (recovered) {
    return (
      <div className="fixed inset-x-4 top-[max(12px,env(safe-area-inset-top))] z-[70] mx-auto flex min-h-11 max-w-sm items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-medium text-white shadow-lg" role="status">
        <CheckCircle2 className="size-4" />
        网络已恢复
      </div>
    );
  }

  if (needRefresh) {
    return (
      <div className="fixed inset-x-4 bottom-[calc(80px+env(safe-area-inset-bottom))] z-[70] mx-auto max-w-sm rounded-2xl border bg-card p-4 shadow-2xl" role="status">
        <p className="text-sm font-semibold">SalesPilot 有新版本</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">更新后会自动重新载入，当前演示数据不会丢失。</p>
        <div className="mt-3 flex gap-2">
          <Button className="flex-1" onClick={() => void updateServiceWorker(true)}>
            <RefreshCw className="size-4" />
            立即更新
          </Button>
          <Button variant="ghost" size="icon" aria-label="稍后更新" onClick={() => setNeedRefresh(false)}>
            <X className="size-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (offlineReady) {
    return (
      <div className="fixed inset-x-4 bottom-[calc(80px+env(safe-area-inset-bottom))] z-[70] mx-auto flex max-w-sm items-center gap-3 rounded-2xl border bg-card p-4 shadow-2xl" role="status">
        <Download className="size-5 text-primary" />
        <p className="min-w-0 flex-1 text-sm font-medium">应用壳已可离线打开</p>
        <Button variant="ghost" size="icon" aria-label="关闭提示" onClick={() => setOfflineReady(false)}>
          <X className="size-4" />
        </Button>
      </div>
    );
  }

  return installPrompt ? null : null;
}
